import { Component, inject, model, resource, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import {
  BbitFileUtils,
  BbitPlaceBookingFileUploadType,
  IBbitI18n,
  IBbitPlaceBookingFileLinkRequest,
  IBbitPlaceBookingMetaResponse,
  IBbitPlaceBookingRequestApprovalRequest,
  IBbitPrepareFileUploadRequest,
  IBbitPrepareFileUploadResponse,
} from '@bbitgmbh/bbit.utils';
import { BbitWebsiteService, I18nPipe } from '@bbitgmbh/website.sdk';
import { NgIcon } from '@ng-icons/core';
import { LoadingComponent } from '../../helper/loading/loading.component';
import { PlaceBookingService } from '../booking.service';
import { KnowHowUploadComponent } from '../know-how-upload/know-how-upload.component';
import { BookingInfoOverlayRef } from './booking-info-overlay.ref';

export enum PlaceBookingStepInput {
  FILE = 'file',
  OPTIONAL_MULTI_FILE = 'optional-multi-file',
  TERMS = 'terms',
  SUBMIT = 'submit',
}

type Step = ITermsStep | IFileStep | ISubmitStep | IOptionalMultiFileStep;

interface IBaseStep {
  _id: string;
  input: PlaceBookingStepInput;
  title: IBbitI18n;
  description: IBbitI18n;
}

interface ISubmitStep extends IBaseStep {
  input: PlaceBookingStepInput.SUBMIT;
}

interface ITermsStep extends IBaseStep {
  input: PlaceBookingStepInput.TERMS;
  signal: WritableSignal<boolean>;
  url: string;
}

interface IFileStep extends IBaseStep {
  fileType: BbitPlaceBookingFileUploadType;
  input: PlaceBookingStepInput.FILE;
}

interface IOptionalMultiFileStep extends IBaseStep {
  fileType: BbitPlaceBookingFileUploadType;
  input: PlaceBookingStepInput.OPTIONAL_MULTI_FILE;
  filesUploadedSignal: WritableSignal<boolean>;
}

interface IMetaRequest {
  acceptedRules: boolean;
  knowHowFilesUploaded: boolean;
}

@Component({
  selector: 'app-booking-info-overlay',
  templateUrl: './booking-info-overlay.component.html',
  styleUrl: './booking-info-overlay.component.scss',
  imports: [NgIcon, FormsModule, LoadingComponent, I18nPipe, KnowHowUploadComponent],
})
export class BookingInfoOverlayComponent {
  private _overlayRef = inject(BookingInfoOverlayRef);
  private _website = inject(BbitWebsiteService);

  public currentStep = signal<number>(0);
  public displayMode = signal<'loggedOut' | 'notVerified' | 'hasPendingApprovalRequest'>('notVerified');
  public isUploading = signal(false);
  private _service = inject(PlaceBookingService);
  public BbitPlaceBookingFileUploadType = BbitPlaceBookingFileUploadType;
  public PlaceBookingStepInput = PlaceBookingStepInput;
  public acceptedRules = signal(false);
  public knowHowFilesUploadedSignal = signal(false);
  public possibleDates = model<string>();
  public meta = resource<IBbitPlaceBookingMetaResponse, IMetaRequest>({
    params: () => {
      return {
        acceptedRules: this.acceptedRules(),
        knowHowFilesUploaded: this.knowHowFilesUploadedSignal(),
      };
    },
    loader: async ({ params }) => {
      const meta = await this._service.getMeta();
      if (meta.hasPendingApprovalRequest) {
        this.displayMode.set('hasPendingApprovalRequest');
      } else if (!meta.isAllowedToBook) {
        this.displayMode.set('notVerified');
      }
      if (!params.acceptedRules) {
        this.currentStep.set(0);
      } else if (meta.files?.[BbitPlaceBookingFileUploadType.ID_OR_PASSPORT]?.length === 0) {
        this.currentStep.set(1);
      } else if (meta.files?.[BbitPlaceBookingFileUploadType.LIABILITY_INSURANCE]?.length === 0) {
        this.currentStep.set(2);
      } else if (meta.files?.[BbitPlaceBookingFileUploadType.WEAPON_OWNERSHIP_CONFIRMATION]?.length === 0) {
        this.currentStep.set(3);
      } else if (!params.knowHowFilesUploaded) {
        this.currentStep.set(4);
      } else {
        this.currentStep.set(5);
      }

      return meta;
    },
  });

  public fileSteps: Step[] = [
    {
      _id: 'gtcs',
      input: PlaceBookingStepInput.TERMS,
      title: 'AGBs',
      description: 'Bitte lies unsere AGBs aufmerksam durch und bestätige, dass du diese akzeptierst.',
      signal: this.acceptedRules,
      url: 'https://bt-shop.ch/agb/',
    },
    {
      _id: 'idOrPassport',
      fileType: BbitPlaceBookingFileUploadType.ID_OR_PASSPORT,
      input: PlaceBookingStepInput.FILE,
      title: 'ID oder Pass',
      description:
        'Um deinen Account freizugeben, benötigen wir ein Scan oder Foto von deiner ID oder deinem Pass. Bitte stelle sicher, dass die Datei gut lesbar ist und die ID/Pass-Nummer ersichtlich ist.\n\nEs kann nur eine Datei hochgeladen werden.',
    },
    {
      _id: 'liabilityInsurance',
      fileType: BbitPlaceBookingFileUploadType.LIABILITY_INSURANCE,
      input: PlaceBookingStepInput.FILE,
      title: 'Haftpflichtversicherung',
      description:
        "Du benötigst eine Haftpflichtversicherung mit einer Deckung von mindestens CHF 5'000'000.00 damit du unsere Schiessanalgen nutzen kannst. Bitte lade hier eine entsprechende Bestätigung deiner Versicherung hoch (aktive Versicherungspolice mit ersichtlicher Deckungshöhe).\n\nEs kann nur eine Datei hochgeladen werden.",
    },
    {
      _id: 'weaponOwnershipConfirmation',
      fileType: BbitPlaceBookingFileUploadType.WEAPON_OWNERSHIP_CONFIRMATION,
      input: PlaceBookingStepInput.FILE,
      title: 'Waffenbesitzbestätigung',
      description:
        'Bitte lade hier ein Dokument hoch, welches bestätigt, dass du in der Schweiz eine Waffe besitzen darfst. Dazu zählen Dokumente wie ein Strafregisterauszug (nicht älter als drei Monate), Waffenerwerbschein (nicht älter als zwei Jahre), Ausnahmebewilligung klein/gross (nicht älter als zwei Jahre), Polizeiausweis, etc.\n\nEs kann nur eine Datei hochgeladen werden.',
    },
    {
      _id: 'knowHowDocument',
      fileType: BbitPlaceBookingFileUploadType.KNOW_HOW_DOCUMENT,
      input: PlaceBookingStepInput.OPTIONAL_MULTI_FILE,
      title: 'Ausbildungsnachweise',
      description:
        'Um bei uns selbständig schiessen zu dürfen musst du nachweisen, dass du mindestens eine Grundausbildung an einer Feuerwaffe absolviert hast. Bitte lade hier einen Ausbildungsnachweis der höchsten von dir absolvierten Ausbildungsstufe hoch – du kannst auch mehrere Ausbildungsnachweise hochladen.\n\nJe nach Ausbildungsstand, den du nachweisen kannst erhältst du mehr oder weniger Freigaben zur Nutzung unserer Schiessanlagen (statisch schiessen/ dynamisches schiessen / Leiten von Übungen usw.).\n\nFalls du noch keine Ausbildung an einer Feuerwaffe hattest oder diese einfach nicht nachweisen kannst, kannst du einen entsprechenden Kurs oder Test bei uns oder einem unserer Partner absolvieren. Klicke hierfür auf «Ich habe keine Dateien» und mache bitte einen entsprechenden Vermerk beim Termin im nächsten Schritt.',
      filesUploadedSignal: this.knowHowFilesUploadedSignal,
    },
    {
      _id: 'submit',
      input: PlaceBookingStepInput.SUBMIT,
      title: 'Abschluss',
      description:
        'Vielen Dank für deine Angaben, du hast es schon fast geschafft. Nun benötigst du noch einen Termin bei uns im Ladenlokal, um die Einweisung zur Anlagennutzung zu erhalten – Dauer ca. 30min.\n\nUm die Terminfindung zu vereinfachen, bitten wir dich uns unten zwei bis drei dir mögliche Termine innerhalb unserer Ladenöffnungszeiten vorzuschlagen. Nach dem Abschliessen der Anmeldung wirst du von uns schnellstmöglich betreffend der angegebenen Termine kontaktiert.\n\nWir bitten um Verständnis, dass sehr kurzfristige Termine nur in Ausnahmefällen möglich sind.',
    },
  ];

  constructor() {
    this._website
      .observeProfile()
      .pipe(takeUntilDestroyed())
      .subscribe(async (profile) => {
        if (!profile) {
          this.displayMode.set('loggedOut');
          this.meta.destroy();
          return;
        }
        if (!profile?.placeBooking?.isAllowedToBook) {
          this.meta.reload();
        }
      });
  }

  dragLeave(event: Event) {
    const element = event.target as HTMLInputElement;
    element.classList.add('border-dashed');
    element.classList.remove('border-solid');
    element.classList.remove('bg-primary-600');
    event.preventDefault();
    event.stopPropagation();
  }

  async uploadFile(event: DragEvent | Event, type: BbitPlaceBookingFileUploadType): Promise<void> {
    try {
      this.isUploading.set(true);
      event.preventDefault();
      event.stopPropagation();

      const element = event.target as HTMLInputElement;
      element.classList.add('border-dashed');
      element.classList.remove('border-solid');
      element.classList.remove('bg-primary-600');
      const inputFile = (event as DragEvent)?.dataTransfer?.files?.[0] || element?.files?.[0];
      if (inputFile) {
        // check file type
        const fileName = inputFile.name?.toLowerCase();
        if (fileName) {
          const supportedFormats = ['pdf', 'png', 'jpg', 'jpeg'];
          const ext = fileName.split('.').pop();
          if (ext && supportedFormats.indexOf(ext) === -1) {
            return;
          }
        }
        const file = BbitFileUtils.createAttachmentFromFile(inputFile);
        const prepareBody: IBbitPrepareFileUploadRequest = {
          fileName: file.fileName,
          contentType: file.contentType,
          contentLength: file.contentLength || 0,
        };
        const prepareResult = await fetch(`${this._website.getBlApiUrl()}/account/prepareFileUpload`, {
          method: 'POST',
          headers: {
            Authorization: `${this._website.getAccessToken()}`,
          },
          body: JSON.stringify(prepareBody),
        });
        if (!prepareResult.ok) {
          throw new Error("Couldn't prepare file upload");
        }
        const prepareResponse: IBbitPrepareFileUploadResponse = await prepareResult.json();

        const putResult = await fetch(prepareResponse.putUrl, {
          method: 'PUT',
          body: inputFile,
          headers: {
            'Content-Type': inputFile.type,
          },
        });
        if (!putResult.ok) {
          throw new Error("Couldn't upload file");
        }
        const linkBody: IBbitPlaceBookingFileLinkRequest = {
          type,
          file: prepareResponse.file,
        };
        const linkResult = await fetch(`${this._website.getBlApiUrl()}/placeBooking/linkFile`, {
          method: 'POST',
          headers: {
            Authorization: `${this._website.getAccessToken()}`,
          },
          body: JSON.stringify(linkBody),
        });
        if (!linkResult.ok) {
          throw new Error("Couldn't link file to contact");
        }
        this.meta.reload();
      }
    } catch (err) {
      console.error(err);
    } finally {
      this.isUploading.set(false);
    }
  }

  dragOver(event: DragEvent) {
    const element = event.target as HTMLInputElement;
    element.classList.remove('border-dashed');
    element.classList.add('border-solid');
    element.classList.add('bg-primary-600');
    // element.style.border = '1px solid hsl(130, 100%, 26%)';
    event.preventDefault();
    event.stopPropagation();
  }

  login(): void {
    this._website.login();
  }

  close(): void {
    this._overlayRef.close();
  }

  public isDone(step: Step): boolean {
    switch (step.input) {
      case PlaceBookingStepInput.FILE:
        return (this.meta.value()?.files?.[step.fileType]?.length || 0) > 0;
      case PlaceBookingStepInput.OPTIONAL_MULTI_FILE:
        return step.filesUploadedSignal();
      case PlaceBookingStepInput.TERMS:
        return step.signal();
    }
    return false;
  }

  public async submit(): Promise<void> {
    const body: IBbitPlaceBookingRequestApprovalRequest = {
      possibleDates: this.possibleDates(),
    };
    const result = await fetch(`${this._website.getBlApiUrl()}/placeBooking/requestApproval`, {
      method: 'POST',
      headers: {
        Authorization: `${this._website.getAccessToken()}`,
      },
      body: JSON.stringify(body),
    });
    if (!result.ok) {
      throw new Error("Couldn't submit data");
    }
    this.meta.reload();
  }
}
